import { useCallback, useEffect, useMemo, useState } from 'react';

import type { InfiniteData } from '@tanstack/react-query';

import { KEYBOARD_KEYS } from '@/shared/lib/constants/keyboard';

import type { SearchResult } from '@/entities/search';
import { useSearchStore } from '@/entities/search';

import { useAssetPreview } from '@/features/search/model';
import { getUniqueKey } from '@/features/search/model/_internal/cacheManager';

type SearchPageData = {
  items: SearchResult[];
  totalCount: number;
  nextPage?: number;
  aggregations: unknown;
};

type UseSearchResultsContainerParams = {
  data: InfiniteData<SearchPageData> | undefined;
  hasNextPage: boolean;
  fetchNextPage: () => void;
};

export const useSearchResultsContainer = ({ data, hasNextPage, fetchNextPage }: UseSearchResultsContainerParams) => {
  const { query } = useSearchStore();
  const [selectedResult, setSelectedResult] = useState<SearchResult | null>(null);

  const allResults = useMemo(() => {
    const flat = data?.pages.flatMap((page: SearchPageData) => page.items) ?? [];
    const seen = new Set<string>();
    const deduped: SearchResult[] = [];
    for (const item of flat) {
      const key = getUniqueKey(item);
      if (!seen.has(key)) {
        seen.add(key);
        deduped.push(item);
      }
    }
    return deduped;
  }, [data?.pages]);
  const totalCount = data?.pages[0]?.totalCount ?? 0;

  const {
    infoRows,
    sections,
    description,
    isLoading: isPreviewLoading,
    isError: isPreviewError,
    refetch: refetchPreview,
  } = useAssetPreview({
    selectedResult,
    sourceData: selectedResult?._source,
  });

  useEffect(() => {
    if (allResults.length > 0) {
      setSelectedResult((prev) => {
        if (!prev || !allResults.find((r: SearchResult) => r.id === prev.id)) {
          return allResults[0];
        }
        return prev;
      });
    } else {
      setSelectedResult(null);
    }
  }, [allResults]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (allResults.length === 0) return;

      const currentIndex = selectedResult ? allResults.findIndex((r: SearchResult) => r.id === selectedResult.id) : -1;

      if (e.key === KEYBOARD_KEYS.ARROW_DOWN) {
        e.preventDefault();
        const nextIndex = currentIndex < allResults.length - 1 ? currentIndex + 1 : 0;
        setSelectedResult(allResults[nextIndex]);
      } else if (e.key === KEYBOARD_KEYS.ARROW_UP) {
        e.preventDefault();
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : allResults.length - 1;
        setSelectedResult(allResults[prevIndex]);
      }
    },
    [allResults, selectedResult]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage]);

  return {
    allResults,
    totalCount,
    query,
    selectedResult,
    setSelectedResult,
    infoRows,
    sections,
    description,
    isPreviewLoading,
    isPreviewError,
    refetchPreview,
    handleLoadMore,
  };
};
